import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LatestReport } from '../utils/RestoApiService';
import { DateFormatter } from '../utils/DateFormatter';
import { buildApiUrl } from '../utils/ApiConfig';
import { generateRestoSlug } from '../utils/RestoSlugUtils';

interface RecentReportItemProps {
  report: LatestReport;
}

// Helper function to get emoji based on rating
const getEmojiForRating = (rating: number): string => {
  switch (rating) {
    case 1:
      return "\u{1F480}"; // skull
    case 2:
      return "\u{1F92E}"; // face vomiting
    case 3:
      return "\u{1F642}"; // slightly smiling face
    case 4:
      return "\u{1F603}"; // smiling face with open mouth
    case 5:
      return "\u{1F60D}"; // smiling face with heart-eyes
    default:
      return "\u{1F642}"; // default to slightly smiling face
  }
};

const getRatingLabel = (rating: number, t: (key: string) => string): string => {
  switch (rating) {
    case 1: return t('ratings.terrible');
    case 2: return t('ratings.poor');
    case 3: return t('ratings.okay');
    case 4: return t('ratings.good');
    case 5: return t('ratings.excellent');
    default: return `${rating}/5`;
  }
};


const RecentReportItem: React.FC<RecentReportItemProps> = ({ report }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const relativeTime = DateFormatter.formatRelativeTime(report.createdAt);
  const fullDate = DateFormatter.formatFullDate(report.createdAt);

  // Check if text is long enough to need truncation
  const needsTruncation = report.reportText.length > 200;

  // Build full image URL if imageUrl is provided
  const fullImageUrl = report.imageUrl ? buildApiUrl(report.imageUrl) : null;

  const ratingLabel = getRatingLabel(report.rating, t);

  return (
    <div className="flex flex-col">
      {/* Header: Restaurant name + emoji rating */}
      <div className="flex justify-between items-start gap-3 mb-3">
        {report.restaurantName && report.restaurantId ? (
          <a
            href={`?restoId=${generateRestoSlug(report.restaurantName, report.restaurantId)}`}
            onClick={(e) => {
              e.preventDefault();
              const restoSlug = generateRestoSlug(report.restaurantName!, report.restaurantId!);
              const url = `${window.location.pathname}?restoId=${restoSlug}`;
              window.history.pushState({ restoId: report.restaurantId }, "", url);
              window.dispatchEvent(new CustomEvent('restaurantSelected', {
                detail: {
                  restaurantId: report.restaurantId,
                  openSidebar: true
                }
              }));
            }}
            className="text-base font-semibold text-gray-900 dark:text-gray-50 hover:text-blue-600 dark:hover:text-blue-400 transition-colors leading-snug flex items-center gap-1"
          >
            {report.restaurantName}
            <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
          </a>
        ) : (
          <div className="flex-1" />
        )}
        <div className="flex flex-col items-center flex-shrink-0" role="img" aria-label={ratingLabel}>
          <span className="text-2xl leading-none">{getEmojiForRating(report.rating)}</span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{report.rating}/5</span>
        </div>
      </div>

      {/* Review text */}
      <div className="text-left text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-2 border-l-2 border-blue-200 dark:border-blue-800 pl-3">
        <p className={`${!isExpanded && needsTruncation ? "line-clamp-3" : ""} transition-all duration-300`}>
          {report.reportText}
        </p>

        {needsTruncation && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs mt-1 focus:outline-none"
          >
            {isExpanded ? t('common.readLess') : t('common.readMore')}
          </button>
        )}
      </div>

      {/* Display image if available */}
      {fullImageUrl && (
        <div className="mb-3 relative">
          {isImageLoading && (
            <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          )}
          {imageError ? (
            <div className="bg-gray-200/50 dark:bg-gray-700/50 rounded-lg p-3 flex flex-col items-center justify-center h-24">
              <svg className="w-6 h-6 text-gray-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              <p className="text-xs text-gray-500">Image unavailable</p>
            </div>
          ) : (
            <div className="relative group overflow-hidden rounded-lg cursor-pointer">
              <img
                src={fullImageUrl}
                alt="Report attachment"
                className={`w-full max-h-48 object-cover transition-all duration-300 group-hover:scale-105 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
                onClick={() => window.open(fullImageUrl, '_blank')}
                onLoad={() => setIsImageLoading(false)}
                onError={() => {
                  setIsImageLoading(false);
                  setImageError(true);
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
                <span className="text-xs">View</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer: username + timestamp */}
      <div className="flex items-center gap-1 pt-2 mt-auto">
        <span className="text-[11px] text-gray-400 dark:text-gray-500">{report.username}</span>
        <span className="text-gray-300 dark:text-gray-600 text-[11px]">&middot;</span>
        <span
          className="text-[11px] text-gray-400 dark:text-gray-500 cursor-help"
          title={fullDate}
        >
          {relativeTime}
        </span>
      </div>
    </div>
  );
};

export default RecentReportItem;
